import { ErrorFeedback } from 'components/common-form/sidePanel/priceEnhancer/priceImprovement/actions/errorFeedback';
import { Spacer } from 'components/common-form/sidePanel/priceEnhancer/priceImprovement/actions/spacer';
import { VerticalFormGroupComponent } from 'components/shared/FormGroup/VerticalFormGroupComponent';
import { LightAlert } from 'components/shared/LightAlert';
import { PercentWithUnit } from 'components/shared/Percent/PercentWithUnit';
import { PercentWithUnitAndIncrementers } from 'components/shared/Percent/PercentWithUnitAndIncrementers';
import { ChangeEvent, FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import type { ApiError } from 'utils/http/apiErrorHelper';

import type { PriceImprovementResponseProps } from './priceImprovementResponse.connect';
import { connectPriceImprovementResponse } from './priceImprovementResponse.connect';

const PriceImprovementResponseRaw: FC<PriceImprovementResponseProps> = ({
  isDisabled,
  isTrader,
  targetAdjustedPrice,
  comment,
  acceptablePriceAdjustment,
  validationError,
  rejectionError,
  onRejected,
  onValidated,
  onCommentChanged,
  onAcceptablePriceAdjustmentChanged,
  onErrorDismissed,
}) => {
  const validateDisabled = useMemo(
    () => isDisabled || !Number.isFinite(acceptablePriceAdjustment),
    [acceptablePriceAdjustment, isDisabled],
  );

  const { formatMessage } = useIntl();

  const [commentValue, setCommentValue] = useState<string>(comment || '');

  const onCommentChange = useCallback((e: ChangeEvent<HTMLTextAreaElement>) => setCommentValue(e.target.value), []);

  const onCommentBlur = useCallback(() => {
    onCommentChanged(commentValue);
  }, [commentValue, onCommentChanged]);

  useEffect(() => {
    setCommentValue(comment || '');
  }, [comment]);

  return (
    <>
      <div className="card card-bordered">
        <div className="card-body p-3">
          <div className="row align-items-end">
            <VerticalFormGroupComponent
              className="col-5"
              inputId="requested-price-improvement"
              labelText={formatMessage({ id: 'priceImprovement.requested.price' })}
            >
              <PercentWithUnit
                e2e="requested-price-improvement"
                id="requested-price-improvement"
                unit="%"
                value={targetAdjustedPrice}
                precision={4}
                decimalPadding
                isDisabled
                isReadOnly
              />
            </VerticalFormGroupComponent>
            <VerticalFormGroupComponent
              className="col-7"
              inputId="acceptable-price-improvement"
              labelText={formatMessage({ id: 'priceImprovement.acceptable.price' })}
            >
              <PercentWithUnitAndIncrementers
                e2e="acceptable-price-improvement"
                unit="%"
                value={acceptablePriceAdjustment}
                precision={4}
                incrementStep={0.1}
                id="acceptable-price-improvement"
                decimalPadding
                onChange={onAcceptablePriceAdjustmentChanged}
                onBlur={onAcceptablePriceAdjustmentChanged}
              />
            </VerticalFormGroupComponent>
          </div>
          <textarea
            id="comment"
            value={commentValue}
            onBlur={onCommentBlur}
            onChange={onCommentChange}
            className="form-control"
            data-e2e="price-improvement-comment"
            placeholder={formatMessage({ id: 'priceImprovement.comment.placeholder' })}
            rows={3}
          />
        </div>
      </div>
      {isTrader === true && (
        <TraderActions
          isDisabled={isDisabled}
          validateDisabled={validateDisabled}
          onValidated={onValidated}
          onRejected={onRejected}
          validationError={validationError}
          rejectionError={rejectionError}
          onErrorDismissed={onErrorDismissed}
        />
      )}
      {isTrader === undefined && (
        <div className="d-flex flex-column mb-1 pb-1">
          <LightAlert severity="warning" e2e="validation-warning">
            {formatMessage({ id: 'priceImprovement.warning.trader.role' })}
          </LightAlert>
        </div>
      )}
    </>
  );
};

export const PriceImprovementResponse = connectPriceImprovementResponse(PriceImprovementResponseRaw);

interface TraderActionsProps {
  isDisabled?: boolean;
  validateDisabled: boolean;
  validationError?: ApiError;
  rejectionError?: ApiError;
  onValidated: () => void;
  onRejected: () => void;
  onErrorDismissed: () => void;
}

const TraderActions: FC<TraderActionsProps> = ({
  isDisabled,
  validateDisabled,
  validationError,
  rejectionError,
  onErrorDismissed,
  onValidated,
  onRejected,
}) => {
  const { formatMessage } = useIntl();
  return (
    <div className="mt-3 d-flex flex-row">
      {rejectionError ? (
        <ErrorFeedback
          errorType="rejection"
          error={rejectionError}
          className="flex-grow-1"
          onDimiss={onErrorDismissed}
          position="top"
        />
      ) : (
        <button
          data-e2e="price-improvement-reject-button"
          className="btn btn-xl btn-discreet-secondary flex-grow-1 me-2"
          type="button"
          onClick={onRejected}
          disabled={isDisabled}
        >
          {formatMessage({ id: 'reject' })}
        </button>
      )}
      <Spacer />
      {validationError ? (
        <ErrorFeedback
          errorType="validation"
          error={validationError}
          className="flex-grow-1"
          onDimiss={onErrorDismissed}
          position="top"
        />
      ) : (
        <button
          data-e2e="price-improvement-validate-button"
          className="btn btn-xl btn-info flex-grow-1"
          type="button"
          onClick={onValidated}
          disabled={validateDisabled}
        >
          {formatMessage({ id: 'validate' })}
        </button>
      )}
    </div>
  );
};
